import { VStack } from "@chakra-ui/react";
import { getShortContractAddress } from "@syla/shared/helpers/assets/getShortContractAddress";
import {
  getDataSourceSupportedInputs,
  DataSourceType,
  ImportStatus,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { useQueryDataSources } from "../../../../store/useQueryDataSources";
import { FileDropZoneInput } from "../../../forms/FileDropZoneInput";
import { ImportMethod } from "../panels/ImportMethodSelectionPanel";
import { AddDataSourceForm } from "../workflows/AddDataSourceWorkflow";
import { AssistedFileImportCard } from "./AssistedFileImportCard";
import { AuthoriseConnectionButton } from "./AuthoriseConnectionButton";
import { DataSourceNameField } from "./DataSourceNameField";
import { NetworkSelectorField } from "./NetworkSelectorField";
import { SyncApiFields } from "./SyncApiFields";

export function ImportMethodInputsSection({
  dataSource,
  importMethod,
  onImportError,
  onAuthorised,
}: {
  dataSource: DataSourceWithNetworks;
  importMethod: ImportMethod;
  onImportError: (error: Error) => void;
  onAuthorised: (walletId: string) => void;
}): React.ReactElement | null {
  const form = useFormContext<AddDataSourceForm>();

  const supportedInputs = useMemo(
    () => getDataSourceSupportedInputs(dataSource),
    [dataSource],
  );

  const customName = form.watch("customName");

  useAutoWalletCustomName({
    dataSourceName: dataSource.name,
    dataSourceType: dataSource.type,
  });

  if (importMethod === "Empty") {
    return (
      <DataSourceNameField
        isRequired={true}
        name="customName"
        form={form}
        registerOptions={{
          required: "Please enter a name",
          minLength: 1,
        }}
      />
    );
  }

  if (importMethod === DataSourceInputType.Api) {
    if (!supportedInputs.sync) {
      return null;
    }

    if (dataSource.syncAuthType === "oauth") {
      return (
        <AuthoriseConnectionButton
          w="full"
          dataSource={dataSource}
          customName={customName}
          onAuthorised={onAuthorised}
          onError={onImportError}
        />
      );
    }

    return (
      <VStack w="full" align="flex-start" spacing={4}>
        {dataSource.syncImportParams && (
          <SyncApiFields dataSource={dataSource} />
        )}
        {dataSource.type === DataSourceType.Wallet && (
          <NetworkSelectorField
            form={form}
            name="network"
            networks={dataSource.networks}
          />
        )}
      </VStack>
    );
  }

  if (dataSource.type === DataSourceType.Custom) {
    return (
      <VStack w="full" align="flex-start" spacing={4}>
        <DataSourceNameField
          isRequired={true}
          name="customName"
          form={form}
          registerOptions={{
            required: "Please enter a name",
            minLength: 1,
          }}
        />

        <Controller
          control={form.control}
          name="storedFiles"
          render={({ field }) => (
            <FileDropZoneInput value={field.value} onChange={field.onChange} />
          )}
        />
      </VStack>
    );
  }

  if (importMethod === DataSourceInputType.Csv) {
    if (supportedInputs.file === ImportStatus.Alpha) {
      return <AssistedFileImportCard />;
    }
    return (
      <Controller
        control={form.control}
        name="storedFiles"
        render={({ field }) => (
          <FileDropZoneInput value={field.value} onChange={field.onChange} />
        )}
      />
    );
  }

  if (importMethod === DataSourceInputType.Custom) {
    return (
      <Controller
        control={form.control}
        name="storedFiles"
        render={({ field }) => (
          <FileDropZoneInput value={field.value} onChange={field.onChange} />
        )}
      />
    );
  }

  return null;
}

/**
 * For wallets and blockchains, automatically update the custom name
 * based on the entered address.
 */
function useAutoWalletCustomName({
  dataSourceName,
  dataSourceType,
}: {
  dataSourceName: string;
  dataSourceType: DataSourceType;
}) {
  const { resetField, watch, setValue } = useFormContext<AddDataSourceForm>();

  const shouldAutoGenerateWalletName =
    dataSourceType === DataSourceType.Blockchain ||
    dataSourceType === DataSourceType.Wallet;

  const dataSourcesQuery = useQueryDataSources(shouldAutoGenerateWalletName);

  const apiKey = watch("apiKey")?.trim();
  const networkId = watch("network")?.trim();

  useEffect(() => {
    if (!shouldAutoGenerateWalletName) {
      return;
    }

    if (!apiKey) {
      setValue("customName", dataSourceName);
      return;
    }

    const normalisedAddress = apiKey.replace(/^(bitcoincash:|ecash:)/i, "");

    const networkName = dataSourcesQuery.data?.find(
      (dataSource) => dataSource._id === networkId,
    )?.name;

    setValue(
      "customName",
      `${networkName ?? dataSourceName} (${getShortContractAddress(
        normalisedAddress,
      )})`,
    );
  }, [
    apiKey,
    dataSourcesQuery.data,
    dataSourceName,
    networkId,
    resetField,
    setValue,
    shouldAutoGenerateWalletName,
  ]);
}
